import { useRef, useState } from "react";
import axios from "axios";
import { trackEvent } from "../observability";
import { getPaidSession } from "../utils/paidSessions";
import { isValidUkPlate, normalizePlate } from "../utils/ukPlate";

/**
 * Analysis pipeline flow — extracted verbatim from App.js (Jun 2026 refactor).
 * Owns the analyze job start/poll loop, the add-VIN re-analysis and the
 * "new search with direct Stripe payment" flow. All functions are recreated
 * per render (same closure semantics as the original inline definitions).
 */
export const useAnalyzeFlow = ({
  API,
  t,
  language,
  registration,
  vinNumber,
  targetPrice,
  analysis,
  navigate,
  location,
  setError,
  setLoading,
  setAnalysis,
  setShowVerdict,
  setPdfUnlocked,
  setRegistration,
  setVinNumber,
  setTargetPrice,
  setShowLoadingOverlay,
  setLoadingProgress,
  setLoadingProviders,
  setLoadingContext,
}) => {
  const loadingDataRef = useRef(null);
  const [autoPayAfterAnalysis, setAutoPayAfterAnalysis] = useState(false);

  const analyzeVehicle = async (overrides = null) => {
    // Allow callers (e.g. startNewSearchWithPayment) to bypass closure-stale
    // state when they have a freshly-entered registration / vin / price that
    // hasn't propagated through React yet.
    const reg = (overrides?.registration ?? registration).trim();
    const vin = (overrides?.vin ?? vinNumber);
    const price = (overrides?.price ?? targetPrice);
    if (!reg) {
      setError('errorEmptyRegistration');
      return;
    }
    // Deep-link: push /report/:reg so the URL reflects the analysis and the
    // user can bookmark / refresh / share the page.
    const cleanReg = reg.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (cleanReg && !location.pathname.startsWith(`/report/${cleanReg}`)) {
      navigate(`/report/${cleanReg}`, { replace: false });
    }
    trackEvent('analysis_started', {
      registration_length: reg.length,
      has_vin: vin.length === 17,
      has_asking_price: !!price,
      language,
    });
    if (vin.length === 17) {
      trackEvent('vin_submitted', { length: 17 });
    }

    setLoading(true);
    setError("");
    setAnalysis(null);
    setShowLoadingOverlay(true);
    setLoadingProgress(0);
    setLoadingProviders({ openai: 'pending', anthropic: 'pending', gemini: 'pending' });
    setLoadingContext(null);
    loadingDataRef.current = null;

    // Helper for a smooth LINEAR crawl 0 → 95% over ~55s while we wait.
    // The backend also sends real percent ticks; we take the max of crawl vs real
    // so the bar never stalls and always moves forward smoothly.
    const CRAWL_DURATION_MS = 55_000;
    const CRAWL_CEILING = 95;
    const crawlStartedAt = Date.now();
    let lastRealPct = 0;
    const tickCrawl = () => {
      const elapsed = Date.now() - crawlStartedAt;
      const target = Math.min(CRAWL_CEILING, (elapsed / CRAWL_DURATION_MS) * CRAWL_CEILING);
      setLoadingProgress(p => Math.max(p, Math.max(target, lastRealPct)));
    };
    const crawlTimer = setInterval(tickCrawl, 100);

    try {
      // 1) Start the job
      const startRes = await axios.post(`${API}/analyze/start`, {
        registration: reg,
        language: language,
        subject_price: price ? parseFloat(price) : null,
        vin: vin.length === 17 ? vin : null,
      });
      const jobId = startRes.data?.job_id;
      const wasCached = !!startRes.data?.cached;
      if (!jobId) throw new Error('no job_id');

      // ─── Forward stored paid session_id (if any) to /analyze/status so the
      //     backend serves the FULL payload for the buyer's own re-searches.
      //     Without this, every re-search would be paywall-locked even for
      //     the user who actually paid for this registration.
      //
      //     Special case: if the URL contains ?call_session_id=… (user just
      //     returned from a £2.99 seller-call Stripe redirect), we prefer
      //     that as the auth proof — the backend will unlock the premium
      //     payload because /call-checkout itself was gated on £4.99. This
      //     fixes the "back to £1 paywall after £2.99" regression.
      let authSessionId = null;
      let needsStripeConfirm = false;
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const cs = urlParams.get("call_session_id");
        if (cs && urlParams.get("purpose") === "seller_call") {
          authSessionId = cs;
          needsStripeConfirm = true; // ← we just paid; Stripe webhook may lag
        }
        // Stripe £4.99 PDF return uses ?session_id=…&purpose=pdf
        const sid = urlParams.get("session_id");
        if (!authSessionId && sid && urlParams.get("purpose") === "pdf") {
          authSessionId = sid;
          needsStripeConfirm = true;
        }
      } catch { /* ignore */ }
      if (!authSessionId) {
        const paidSession = getPaidSession(reg);
        authSessionId = paidSession?.sessionId || null;
      }
      // CRITICAL: when the user JUST returned from Stripe (either £4.99 or
      // £2.99), our /api/payment-status hits Stripe synchronously and updates
      // payment_transactions.payment_status → "paid". WITHOUT this pre-confirm
      // step, the subsequent /analyze/status call below sees a still-pending
      // row, treats the session as unpaid, and returns a trimmed £1-tier
      // payload → the user sees the £4.99 paywall AGAIN even though they JUST
      // paid £2.99. We retry up to 4 times with 1.2s backoff to ride out any
      // Stripe-replica lag.
      if (authSessionId && needsStripeConfirm) {
        for (let attempt = 0; attempt < 4; attempt++) {
          try {
            const r = await fetch(`${API}/payment-status/${encodeURIComponent(authSessionId)}`);
            if (r.ok) {
              const j = await r.json();
              if (j?.payment_status === "paid") break;
            }
          } catch { /* ignore — retry */ }
          await new Promise((rs) => setTimeout(rs, 1200));
        }
      }
      const statusQs = authSessionId ? `?session_id=${encodeURIComponent(authSessionId)}` : '';

      // 2) Poll status until done/error. Tight polling for snappy UX.
      //    The preliminary result is delivered ~10-20s in; as soon as it
      //    arrives we bail out of the loading overlay and show the paywall.
      //    The full final result is saved to MongoDB in the background, so
      //    the next time the user hits /analyze/start it's cached.
      const deadline = Date.now() + 180_000; // 3 min safety
      let final = null;
      let sawPreliminary = false;
      while (Date.now() < deadline) {
        const s = await axios.get(`${API}/analyze/status/${jobId}${statusQs}`);
        const sd = s.data || {};
        if (typeof sd.percent === 'number') {
          lastRealPct = Math.max(lastRealPct, sd.percent);
          setLoadingProgress(prev => Math.max(prev, sd.percent));
        }
        if (sd.providers) setLoadingProviders(sd.providers);
        if (sd.loading_context) setLoadingContext(sd.loading_context);

        if (sd.status === 'done') { final = sd.result; break; }
        if (sd.status === 'error') {
          const err = new Error(sd.error?.detail || 'Analysis failed');
          err.response = { status: sd.error?.status_code || 500 };
          throw err;
        }
        // Fast path: as soon as the preliminary result is ready, we can unlock the UI.
        if (!sawPreliminary && sd.preliminary_ready && sd.preliminary_result) {
          sawPreliminary = true;
          final = sd.preliminary_result;
          break;
        }
        await new Promise(r => setTimeout(r, wasCached ? 150 : 700));
      }

      if (!final) throw new Error('timeout');
      loadingDataRef.current = { ok: true, data: final };
    } catch (e) {
      loadingDataRef.current = { ok: false, error: e };
    } finally {
      clearInterval(crawlTimer);
    }

    // Snap to 100% and hold briefly so the animation feels complete
    setLoadingProgress(100);
    await new Promise(r => setTimeout(r, 450));

    // Vibrate on mobile if issues detected
    const result = loadingDataRef.current;
    if (result?.ok && result.data?.risk_level !== 'LOW' && navigator.vibrate) {
      try { navigator.vibrate(80); } catch(_) {}
    }

    // Transition out
    setShowLoadingOverlay(false);

    if (result?.ok) {
      setAnalysis(result.data);
      trackEvent('analysis_completed', {
        registration: registration.trim(),
        risk_level: result.data?.risk_level,
        score: result.data?.worthify_score?.score,
        ai_consensus_count: result.data?.ai_consensus_count,
      });
      localStorage.setItem('pendingAnalysis', JSON.stringify(result.data));
      localStorage.setItem('pendingRegistration', reg);
      if (price) localStorage.setItem('pendingAskingPrice', String(price));
      else localStorage.removeItem('pendingAskingPrice');

      if (autoPayAfterAnalysis) {
        // Direct-pay flow (triggered from New Search modal inside dashboard).
        // Skip verdict screen and jump straight to Stripe checkout.
        setShowVerdict(false);
        setAutoPayAfterAnalysis(false);
        setLoading(false);
        try {
          const originUrl = window.location.origin;
          const resp = await fetch(`${API}/create-checkout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              origin_url: originUrl,
              registration: result.data?.vehicle?.registration || reg,
              purpose: 'pdf'
            })
          });
          const data = await resp.json();
          if (data.url) {
            localStorage.setItem('pendingPdfReg', result.data?.vehicle?.registration || reg);
            localStorage.setItem('pendingPdfLang', language);
            if (price) localStorage.setItem('pendingPdfPrice', price);
            window.location.href = data.url;
            return;
          }
        } catch (err) {
          console.error('Direct-pay after new search failed:', err);
        }
        // Fallback — if Stripe fails, show verdict screen so user can retry
        setShowVerdict(true);
        trackEvent('verdict_screen_viewed', { trigger: 'stripe_direct_pay_failed' });
      } else {
        // ─── £2.99 seller-call Stripe return ──────────────────────────
        // If the URL has ?call_session_id=…&purpose=seller_call, the user
        // JUST paid £2.99 for Nina to call the seller. The analysis above
        // already used that session_id as auth proof so the backend returned
        // the FULL premium payload (pdf_paid=true via
        // _has_paid_session_for_reg seller_call branch). We MUST now:
        //  · Skip the £1 verdict paywall — bouncing the user back to it
        //    when they've already paid £4.99 + £2.99 is the bug the user
        //    is reporting.
        //  · Go straight to the dashboard so NinaCopilot mounts.
        //  · Persist the call session in `worthifyPaidSessions` so a future
        //    refresh of /report/:reg stays unlocked.
        // NinaCopilot's own useEffect then sees `call_session_id` in the URL
        // and fires /api/pine/call-execute, which is what actually places
        // the phone call to the seller.
        let isSellerCallReturn = false;
        try {
          const _up = new URLSearchParams(window.location.search);
          isSellerCallReturn = _up.get("purpose") === "seller_call" && !!_up.get("call_session_id");
          if (isSellerCallReturn) {
            const callSid = _up.get("call_session_id");
            const regForStore = result.data?.vehicle?.registration || reg;
            try {
              const raw = localStorage.getItem('worthifyPaidSessions');
              const all = raw ? JSON.parse(raw) : {};
              // Only overwrite if the existing entry isn't a stronger tier.
              // £4.99 'pdf' session is the long-lived premium proof — DO NOT
              // replace it with a transient seller_call session_id. The
              // backend already grants pdf_paid=true for either purpose, but
              // we want future re-searches to keep using the PDF session as
              // primary auth so the £4.99 receipt stays attached. The
              // seller_call session is stored as a secondary marker in
              // `worthifyActiveCall` below (for the call-resume flow).
              const existing = all[regForStore];
              const existingIsPdf = existing && existing.purpose === 'pdf';
              if (!existingIsPdf) {
                all[regForStore] = { sessionId: callSid, purpose: 'seller_call', ts: Date.now() };
                localStorage.setItem('worthifyPaidSessions', JSON.stringify(all));
              }
            } catch { /* ignore */ }
            // Always persist the active call_session_id so NinaPopup can
            // resume the call even if URL params are cleared. This is a
            // separate key from worthifyPaidSessions (which is the long-lived
            // payment proof keyed by registration). worthifyActiveCall is
            // cleared the moment /pine/call-execute succeeds.
            try { localStorage.setItem('worthifyActiveCall', callSid); } catch { /* ignore */ }
            trackEvent('seller_call_return_unlocked', { registration: regForStore });
          }
        } catch { /* ignore */ }
        if (isSellerCallReturn) {
          setShowVerdict(false);
        } else {
          // ── Already-paid user (£1 verdict or £4.99 premium) ──────────
          // The backend gates the payload strictly by session_id: an
          // unlocked (non-`_paywall_locked`) payload with ai_analysis means
          // this visitor already paid for THIS registration. Bouncing them
          // back to the £1 paywall would make them pay twice — skip
          // straight to the dashboard instead.
          const alreadyPaid = !result.data?._paywall_locked && !!result.data?.ai_analysis;
          if (alreadyPaid) {
            setShowVerdict(false);
            trackEvent('verdict_screen_skipped_already_paid', { registration: reg });
          } else {
            setShowVerdict(true);
            trackEvent('verdict_screen_viewed', { trigger: 'analysis_complete' });
          }
        }
      }
    } else {
      const e = result?.error;
      if (e?.response?.status === 404) {
        setError('errorVehicleNotFound');
      } else if (e?.response?.status === 422) {
        // Validation failure ≠ outage — tell the user to check the plate.
        setError('errorInvalidRegistration');
        trackEvent('reg_validation_failed', {
          input_length: normalizePlate(reg).length,
          source: 'server',
        });
      } else if (e?.response?.status === 429) {
        setError('errorRateLimit');
        trackEvent('rate_limit_hit', { endpoint: 'analyze' });
      } else {
        setError('errorServiceUnavailable');
      }
    }
    setLoading(false);
  };

  // Re-analyze the existing locked result by adding a seller-provided VIN.
  // Lightweight — backend reuses cached AI consensus and only runs the VIN-mismatch
  // scam check. Updates the dashboard in-place so the user immediately sees the
  // VIN-verified badge and any scam banner.
  const reanalyzeWithVin = async (newVin) => {
    const cleanVin = (newVin || "").trim().toUpperCase();
    if (cleanVin.length !== 17) {
      throw new Error(t('vinInvalidLength') || 'VIN must be exactly 17 characters');
    }
    const reg = analysis?.vehicle?.registration || registration;
    if (!reg) throw new Error('Missing registration');
    const res = await axios.post(`${API}/analyze/add-vin`, {
      registration: reg,
      vin: cleanVin,
      language: language,
      subject_price: targetPrice ? parseFloat(targetPrice) : null,
    });
    if (!res.data) throw new Error('No data');
    setAnalysis(res.data);
    setVinNumber(cleanVin);
    localStorage.setItem('pendingAnalysis', JSON.stringify(res.data));
    if (navigator.vibrate) {
      try { navigator.vibrate(res.data?.vin_check?.is_scam_risk ? [60, 60, 60] : 30); } catch(_) {}
    }
    return res.data;
  };

  // New Search from inside the dashboard: enters Reg + VIN in a modal,
  // resets state and auto-triggers analysis + direct Stripe checkout.
  const startNewSearchWithPayment = (newReg, newVin, newAsking) => {
    const cleanReg = (newReg || "").trim().toUpperCase();
    if (!cleanReg) return;
    // Clear all previous analysis context
    setAnalysis(null);
    setShowVerdict(false);
    setPdfUnlocked(false);
    setError("");
    localStorage.removeItem('worthifyPdfUnlockedReg');
    // Prefill form state
    setRegistration(cleanReg);
    setVinNumber((newVin || "").trim().toUpperCase());
    setTargetPrice(newAsking ? String(newAsking) : "");
    // Mark next analysis to auto-jump to Stripe
    setAutoPayAfterAnalysis(true);
    // Kick off analysis on next tick (state must flush first). Pass the
    // freshly-entered registration / vin / price as overrides so the
    // closure in analyzeVehicle never reads stale React state.
    setTimeout(() => {
      analyzeVehicle({
        registration: cleanReg,
        vin: (newVin || "").trim().toUpperCase(),
        price: newAsking ? String(newAsking) : "",
      });
    }, 0);
  };

  return { analyzeVehicle, reanalyzeWithVin, startNewSearchWithPayment };
};
